import type { ArtifactType } from '@/types/api'

export interface ArtifactDef {
  type: ArtifactType
  label: string
  description: string
}

/**
 * The 9 artifact types backed by `POST /v1/startups/{id}/artifacts`.
 * Order matches FRONTEND-SPEC §Screen 4 — pitch deck → analysis → plans
 * → models. Labels and descriptions feed the artifact grid; the type
 * literal is the only thing wired to the API.
 */
export const ARTIFACT_CATALOG: ArtifactDef[] = [
  {
    type: 'pitch_deck',
    label: 'Pitch Deck Outline',
    description:
      'Structured outline covering problem, solution, market, traction, team, and ask.',
  },
  {
    type: 'go_to_market_strategy',
    label: 'GTM Strategy',
    description:
      'Go-to-market plan based on your target market, business model, and competitive positioning.',
  },
  {
    type: 'one_pager',
    label: 'Sales One-Pager',
    description:
      'Customer-facing overview of your product, pricing, and value proposition.',
  },
  {
    type: 'competitor_analysis',
    label: 'Competitor Analysis',
    description:
      'Structured comparison of your competitive landscape, positioning, and moat.',
  },
  {
    type: 'business_model_canvas',
    label: 'Business Model Canvas',
    description:
      'Nine-block canvas summarizing your revenue model, partnerships, and operations.',
  },
  {
    type: 'lean_canvas',
    label: 'Lean Canvas',
    description:
      'Lean-startup canvas focused on problem, solution, key metrics, and unfair advantage.',
  },
  {
    type: 'user_persona',
    label: 'User Persona',
    description:
      'Profile of your primary persona — pains, goals, behaviours, and willingness to pay.',
  },
  {
    type: 'product_roadmap',
    label: 'Product Roadmap',
    description:
      'Sequenced milestones from MVP to next major release, scoped against your features list.',
  },
  {
    type: 'financial_model',
    label: 'Financial Model',
    description:
      'Revenue, runway, and operating-cost projections derived from your fundraising stage.',
  },
]

export const ARTIFACT_BY_TYPE: Record<ArtifactType, ArtifactDef> =
  ARTIFACT_CATALOG.reduce(
    (acc, def) => {
      acc[def.type] = def
      return acc
    },
    {} as Record<ArtifactType, ArtifactDef>
  )

export interface ContextFileDef {
  /** Short uppercase tag rendered in the icon square. */
  iconTag: string
  filename: string
  description: string
}

/**
 * Phase 2 AI-context-file cards. Backend `/context/generate` endpoint
 * is a P1 flag (see [docs/VIDIT-QUESTIONS-2026-04-19.md]); cards render
 * inert until it ships. The list is hard-coded — we only ever expand
 * it when a real export format lands.
 */
export const CONTEXT_FILES: ContextFileDef[] = [
  {
    iconTag: 'C',
    filename: 'CLAUDE.md',
    description:
      'Context file for Claude Code and Claude Desktop in Claude’s preferred format.',
  },
  {
    iconTag: 'ctx',
    filename: 'context.md',
    description:
      'General-purpose context file for any AI tool — portable markdown snapshot.',
  },
  {
    iconTag: '.cr',
    filename: '.cursorrules',
    description:
      'Rules file for Cursor AI, optimized for code-generation context.',
  },
  {
    iconTag: 'A',
    filename: 'AGENTS.md',
    description:
      'Cross-tool standard context file — works with Claude Code, Cursor, and other agents.',
  },
]
